import { API_ENDPOINTS } from '../config/constants';
import { apiFetch } from './common';

export interface StorageFileResponse {
  id: string;
  organizationId: string;
  folderId: string | null;
  name: string;
  storageKey: string;
  sizeBytes: number;
  mimeType: string | null;
  checksumSha256: string | null;
  metadata: Record<string, unknown>;
  uploadedBy: string;
  uploadedByName: string | null;
  uploadStatus: string;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface StorageFolderResponse {
  id: string;
  organizationId: string;
  parentId: string | null;
  name: string;
  createdBy: string;
  createdByName: string | null;
  createdAt: string | null;
}

export interface InitiateUploadResponse {
  fileId: string;
  uploadUrl?: string;
  uploadId?: string;
  parts?: Array<{ partNumber: number; uploadUrl: string }>;
}

export interface StorageQuotaResponse {
  usedBytes: number;
  limitBytes: number;
  availableBytes: number;
}

export const StorageAPI = {
  async listFiles(folderId?: string | null, signal?: AbortSignal): Promise<StorageFileResponse[]> {
    const params = folderId ? `?folderId=${folderId}` : '';
    return apiFetch<StorageFileResponse[]>(`${API_ENDPOINTS.STORAGE.FILES}${params}`, { signal });
  },

  async listFolders(
    parentId?: string | null,
    signal?: AbortSignal,
  ): Promise<StorageFolderResponse[]> {
    const params = parentId ? `?parentId=${parentId}` : '';
    return apiFetch<StorageFolderResponse[]>(`${API_ENDPOINTS.STORAGE.FOLDERS}${params}`, {
      signal,
    });
  },

  async initiateUpload(data: {
    name: string;
    sizeBytes: number;
    contentType: string;
    folderId?: string | null;
  }): Promise<InitiateUploadResponse> {
    return apiFetch<InitiateUploadResponse>(API_ENDPOINTS.STORAGE.UPLOAD, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async completeUpload(
    fileId: string,
    data: {
      uploadId?: string | null;
      parts?: Array<{ partNumber: number; etag: string }> | null;
    },
  ): Promise<{ fileId: string; status: string }> {
    return apiFetch(`${API_ENDPOINTS.STORAGE.FILES}${fileId}/complete`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async getDownloadUrl(fileId: string): Promise<{ downloadUrl: string }> {
    return apiFetch(`${API_ENDPOINTS.STORAGE.FILES}${fileId}/download-url`);
  },

  async deleteFile(fileId: string): Promise<{ deleted: boolean }> {
    return apiFetch(`${API_ENDPOINTS.STORAGE.FILES}${fileId}`, {
      method: 'DELETE',
    });
  },

  async updateFile(
    fileId: string,
    data: { name?: string; folderId?: string | null; metadata?: Record<string, unknown> },
  ): Promise<StorageFileResponse> {
    return apiFetch(`${API_ENDPOINTS.STORAGE.FILES}${fileId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  async createFolder(data: {
    name: string;
    parentId?: string | null;
  }): Promise<StorageFolderResponse> {
    return apiFetch<StorageFolderResponse>(API_ENDPOINTS.STORAGE.FOLDERS, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async deleteFolder(folderId: string): Promise<{ deleted: boolean }> {
    return apiFetch(`${API_ENDPOINTS.STORAGE.FOLDERS}${folderId}`, {
      method: 'DELETE',
    });
  },

  async updateFolder(
    folderId: string,
    data: { name?: string; parentId?: string | null },
  ): Promise<StorageFolderResponse> {
    return apiFetch(`${API_ENDPOINTS.STORAGE.FOLDERS}${folderId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  async getQuota(): Promise<StorageQuotaResponse> {
    return apiFetch<StorageQuotaResponse>(API_ENDPOINTS.STORAGE.QUOTA);
  },

  async getSettingByKey(key: string): Promise<{ value: string }> {
    return apiFetch<{ value: string }>(`/api/v1/settings/key/${key}`);
  },
};
